import { useContext, useEffect, useReducer } from 'react';
import { AuthContext, isAuthSessionValid } from '@zenos/front-end-auth';
import { queryClient } from '@zenos/front-end-util-utils';
import { getEmployeeDetailsQuery } from '@zenos/front-end-data-access';
import { AppContext } from './AppContext.ts';
import type { AppProviderProps } from './types.ts';
import { initialState, reducer } from './reducer.ts';
import { useDeviceType } from './hooks/useDeviceType.ts';

export default function AppProvider({ children }: AppProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { token } = useContext(AuthContext);
  const device = useDeviceType();

  useEffect(() => {
    dispatch({ type: 'SET_DEVICE_TYPE', payload: device });
  }, [device]);

  useEffect(() => {
    if (!isAuthSessionValid(token?.expiry)) return;

    const fetchUser = async () => {
      try {
        const { queryKey, queryFn } = getEmployeeDetailsQuery();
        const employee = await queryClient.fetchQuery({
          queryKey,
          queryFn,
          retry: false,
        });

        dispatch({
          type: 'SET_SELECTED_USER',
          payload: { id: employee.employeeId, name: employee.fullName },
        });
      } catch (error) {
        console.warn('Failed to fetch employee:', error);
      }
    };

    fetchUser();
  }, [token?.expiry]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}
